import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "App";
import {
  ODOO_COMPANY_IDS,
  ODOO_LOCATION_IDS,
  ODOO_LTD_SOURCES,
  ODOO_OPERATION_TYPE_IDS,
  ODOO_WAREHOUSE_IDS,
} from "AppSettings";
import Button, { CharlesButtonWithArrow } from "components/base/Button";
import Errors from "components/base/Errors";

import { Input, NativeDatePicker, Select, Text } from "components/base/Form";
import Page from "components/base/Page";
import { ProductionPlanStatusChain } from "components/base/Status";
import { Alert } from "components/base/Toast";
import ContainerizationView from "components/shared/ContainerizationView";
import OdooDeliveryMethodSelector from "components/shared/OdooDeliveryMethodSelector";
import OdooIncotermsSelector from "components/shared/OdooIncotermsSelector";
import OdooPaymentTermsSelector from "components/shared/OdooPaymentTermsSelector";
import OdooPickingTypeSelector from "components/shared/OdooPickingTypeSelector";
import OdooStockLocationSelector from "components/shared/OdooStockLocationSelector";
import OdooTaxSelector from "components/shared/OdooTaxSelector";
import OdooWarehouseSelector from "components/shared/OdooWarehouseSelector";
import { GENERATE_DOCUMENT } from "graphql/mutations";
import { useModals } from "ModalProvider";
import moment from "moment/moment";
import { OdooContext } from "OdooProvider";
import { currencySymbol } from "pages/price/PriceWithCurrency";
import { createContext, useContext, useState } from "react";
import { BsCheckCircleFill } from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import http from "utils/http";
import { OS_CONFIRM_COST, OS_CONFIRM_TIME } from "utils/permissions";
import AdditionPlansView from "./AdditionPlansView";
import CancelPlanView from "./CancelPlanView";
import ChangeStartDateView from "./ChangeStartDateView";
import CombinedPlanSummary from "./CombinedPlanSummary";
import OdooView from "./components/OdooView";
import PlanTypeSelector from "./components/PlanTypeSelector";
import ConfirmPlanView from "./ConfirmPlanView";
import { COMPLIANCE_DOCUMENTS_OPTIONS } from "./const";
import CreateAdditionPlanView from "./CreateAdditionPlanView";
import AboutOSView from "./docs/AboutOSView";
import DuplicatePlanView from "./DuplicatePlanView";
import {
  COMPUTE_PRODUCTION_PLAN,
  SAVE_PRODUCTION_PLAN,
  UPDATE_PRODUCTION_PLAN_STATE,
} from "./graphql";
import HandlePlanView from "./HandlePlanView";
import INCFOBOrderView from "./inc/INCFOBOrderView";
import IncOdooView from "./inc/IncOdooView";
import IncPoView from "./inc/IncPoView";
import PlanLines from "./PlanLines";
import PlanLogsView from "./PlanLogsView";
import PlanPageNavigationTitleView from "./PlanPageNavigationTitleView";
import ProductionsView from "./ProductionsView";
import ProductProductionInfoView from "./ProductProductionInfoView";
import ReduceQtyView from "./ReduceQtyView";
import RejectPlanView from "./RejectPlanView";
import StartProcessingView from "./StartProcessingView";
import SyncPlanToOdooView from "./syncPlanToOdooViews";
import SyncPriceToPriceListView from "./SyncPriceToPriceListView";
import TryRecomputeView from "./TryRecomputeView";

const NEED_TO_RECOMPUTE_MESSAGE = `You have changed products or start date for this plan, or maybe other plans effected this plan ready date, please "Compute Ready Date" before request to confirm.`;

export const PlanContext = createContext({});

const PlanForm = ({ originalPlan, disabled }) => {
  const { hasPermission } = useContext(AppContext);
  const { odooUrl } = useContext(OdooContext);
  const { user } = useContext(AppContext);

  let currencySymbolString = "USD";
  if (originalPlan.wisPriceList?.currency) {
    currencySymbolString = currencySymbol(originalPlan.wisPriceList.currency);
  }

  const [hasPrepaymentInPaymentTerm, setHasPrepaymentInPaymentTerm] = useState(
    originalPlan.hasPrepaymentInPaymentTerm,
  );
  const [requiredCertificateTypes, setRequiredCertificateTypes] = useState(
    originalPlan.requiredCertificateTypes || "",
  );
  const planRequiredCertificates = requiredCertificateTypes
    ? requiredCertificateTypes.split(",")
    : [];

  const [odooTransferOperationTypeId, setOdooTransferOperationTypeId] =
    useState(originalPlan.odooTransferOperationTypeId);
  const [odooTransferFromLocationId, setOdooTransferFromLocationId] = useState(
    originalPlan.odooTransferFromLocationId,
  );
  const [odooTransferToLocationId, setOdooTransferToLocationId] = useState(
    originalPlan.odooTransferToLocationId,
  );
  const [odooSalesOrderWarehouseId, setOdooSalesOrderWarehouseId] = useState(
    originalPlan.odooSalesOrderWarehouseId,
  );
  const [odooSalesOrderPaymentTermsId, setOdooSalesOrderPaymentTermsId] =
    useState(originalPlan.odooSalesOrderPaymentTermsId);
  const [odooTaxId, setOdooTaxId] = useState(originalPlan.odooTaxId);
  const [odooXDeliverymethod, setOdooXDeliverymethod] = useState(
    originalPlan.odooXDeliverymethod,
  );
  const [odooIncoterm, setOdooIncoterm] = useState(originalPlan.odooIncoterm);
  const [odooSalesOrderClientOrderRef, setOdooSalesOrderClientOrderRef] =
    useState(originalPlan.odooSalesOrderClientOrderRef);
  const [odooSalesOrderSourceId, setOdooSalesOrderSourceId] = useState(
    originalPlan.odooSalesOrderSourceId,
  );

  const [description, setDescription] = useState(originalPlan.description);
  const navigate = useNavigate();

  const [extraData, setExtraData] = useState(
    JSON.parse(originalPlan.extraData),
  );

  function onChangeStockPickingType(id) {
    const pickingTypeId = Number(id);
    setOdooTransferOperationTypeId(pickingTypeId);
    if (pickingTypeId === ODOO_OPERATION_TYPE_IDS.CHINA_INTERNAL_TRANSFER) {
      setOdooTransferFromLocationId(ODOO_LOCATION_IDS.CHINA_STOCK);
    } else if (
      pickingTypeId === ODOO_OPERATION_TYPE_IDS.CAMBODIA_INTERNAL_TRANSFER
    ) {
      setOdooTransferFromLocationId(ODOO_LOCATION_IDS.CAMBODIA_STOCK);
    }
  }

  const [lines, setLines] = useState(originalPlan.lines);

  const [computeQuery, computeQueryRes] = useMutation(COMPUTE_PRODUCTION_PLAN, {
    variables: { id: originalPlan.id },
    onError(error) {
      Alert("error", error.message);
    },
  });

  const [isComputing, setIsComputing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRequestingToConfirm, setIsRequestingToConfirm] = useState(false);
  const [isConfirmingTime, setIsConfirmingTime] = useState(false);
  const [isConfirmingCost, setIsConfirmingCost] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const modal = useModals();
  const reComputeModal = useModals();
  const confirmPlanModal = useModals();
  const additionalPlanModal = useModals();
  const syncToOdooModal = useModals();
  const reduceQtyModal = useModals();
  const bomModal = useModals();
  const syncPriceModal = useModals();
  const logsModal = useModals();

  function showLogs() {
    logsModal.present({
      title: "Compute Logs",
      maxWidth: "max-w-6xl",
      children: <PlanLogsView planId={originalPlan.id} />,
    });
  }

  function validatePlan() {
    const hasAtLeastOneProductWithData = lines.some(
      (line) => line?.product?.id && Number(line.qty) > 0,
    );
    if (!hasAtLeastOneProductWithData) {
      window.alert("Please add at least one product with quantity.");
      return false;
    }

    if (originalPlan.planType === "AB_LTD_SALES_ORDER") {
      // make sure warehouse is selected.
      if (!odooSalesOrderWarehouseId) {
        Alert("error", "Please select a warehouse for this sales order.");
        return false;
      }
    }

    return true;
  }

  async function computeHandler({ alert = true }) {
    if (!validatePlan()) return;

    setIsComputing(true);

    try {
      await savePlan();
      const res = await computeQuery();
      const nextLines = res?.data?.computeProductionPlan?.productionPlan?.lines;
      if (!Array.isArray(nextLines)) {
        return;
      }
      setLines(nextLines);
      if (alert) Alert("success", "Compute finish, auto saved.");
    } catch (error) {
      Alert("error", parseError(error));
    } finally {
      setIsComputing(false);
    }
  }

  const [showProductions, setShowProductions] = useState(false);

  const [saveProductionPlan, saveProductionPlanRes] =
    useMutation(SAVE_PRODUCTION_PLAN);

  const [updatePlan, updatePlanRes] = useMutation(
    UPDATE_PRODUCTION_PLAN_STATE,
    {
      onError(error) {
        Alert("error", error.message);
      },
    },
  );

  const [generateDocument] = useMutation(GENERATE_DOCUMENT, {
    onError: (error) => Alert("error", error.message),
  });

  async function salesConfirmHandler() {
    if (
      originalPlan.wisCustomer.system === "LTD" &&
      originalPlan.odooSalesOrderSourceId === 0
    ) {
      window.alert(
        "Please select a source for this LTD sales order and save before confirming.",
      );
      return;
    }

    let message = "Are you sure to Confirm the plan?";
    if (originalPlan.skipCustomerStages)
      message = "Are you sure request to confirm this plan by production team?";

    if (originalPlan.planType.includes("INC_"))
      message +=
        " A draft PO will be created or updated in INC Odoo once confirmed.";

    if (window.confirm(message)) {
      try {
        await updatePlan({
          variables: { id: originalPlan.id, action: "SALES_CONFIRM" },
        });
        Alert("success", "Plan confirmed by sales successfully.");
      } catch (error) {
        Alert("error", parseError(error));
      }
      setIsRequestingToConfirm(false);
    }
  }

  async function sentToCustomerHandler() {
    if (
      window.confirm(
        "Are you sure update this plan? Once updated, this plan will be mark as `AWAITING CUSTOMER`.",
      )
    ) {
      updatePlan({
        variables: { id: originalPlan.id, action: "SENT_TO_CUSTOMER" },
        onCompleted() {
          Alert("success", "Plan state updated successfully.");
        },
      });
    }
  }

  function customerConfirmHandler(action) {
    let message;
    if (action === "CUSTOMER_CONFIRM") {
      // check if payment terms has been selected.
      if (!originalPlan.odooSalesOrderPaymentTermsId) {
        window.alert("Please select payment terms and save before confirming.");
        return;
      }

      message = "Are you sure you to mark this plan as Customer Confirmed?";
    }
    if (action === "PREPAYMENT_RECEIVED") {
      message =
        "Are you sure you to confirm this plan prepayment received? Once confirmed, this plan will be changed to `AWAITING PRODUCTION` state.";
    }

    if (window.confirm(message)) {
      updatePlan({
        variables: { id: originalPlan.id, action },
        onCompleted() {
          Alert("success", "Plan state updated successfully.");
        },
      });
    }
  }

  async function confirmCostHandler() {
    if (window.confirm("Are you sure you want to confirm the cost?")) {
      setIsConfirmingCost(true);
      try {
        await updatePlan({
          variables: { id: originalPlan.id, action: "CONFIRM_COST" },
        });
      } catch (error) {
        Alert("error", error.message);
      }
      Alert("success", "Cost Confirmed.");
      setIsConfirmingCost(false);
    }
  }

  function tryConfirmTime() {
    confirmPlanModal.present({
      title: "Confirm Time",
      children: (
        <ConfirmPlanView
          plan={originalPlan}
          didConfirmTime={didConfirmTimeHandler}
        />
      ),
    });
  }

  async function didConfirmTimeHandler() {
    confirmPlanModal.hide();

    // If this plan is an additional plan, present sync to odoo modal.
    if (originalPlan.parent) {
      syncToOdooModal.present({
        title: "Sync Plan to Odoo",
        maxWidth: "max-w-6xl",
        children: (
          <SyncPlanToOdooView
            planId={originalPlan.parent.id}
            odooObject={originalPlan.odooObject}
            didSyncedToOdoo={didSyncedToOdoo}
          />
        ),
      });
    }
  }

  function didSyncedToOdoo() {
    if (originalPlan.parent) {
      // If this plan is an additional plan, after synced to odoo, we need to update the plan state to `PROCESSING`
      updatePlan({
        variables: { id: originalPlan.id, action: "START_PROCESSING" },
      });
    }
  }

  async function downloadPdf() {
    if (!originalPlan.readyDate) {
      Alert("error", NEED_TO_RECOMPUTE_MESSAGE);
      return;
    }
    setDownloading(true);
    try {
      await savePlan();
      let res = await generateDocument({
        variables: {
          docType: "production_plan",
          name: originalPlan.name,
          data: JSON.stringify({ plan_id: originalPlan.id }, "", 4),
        },
      });
      res = await http.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}generated-document/${res.data.generateDocument.document.id}/?pdf=1`,
        {
          responseType: "blob",
          withCredentials: true,
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${originalPlan.name}_${new Date().toLocaleString()}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      Alert("error", parseError(error));
    }

    setDownloading(false);
  }

  function changePlanStartDate() {
    modal.present({
      title: "Change Start Date",
      center: true,
      children: (
        <ChangeStartDateView
          plan={originalPlan}
          onCompleted={() => {
            modal.hide();
          }}
        />
      ),
    });
  }

  function cancelPlan() {
    modal.present({
      title: "Cancel Plan",
      center: true,
      maxWidth: "max-w-4xl",
      children: <CancelPlanView plan={originalPlan} onCompleted={modal.hide} />,
    });
  }

  function rejectPlan() {
    modal.present({
      title: "Reject Plan",
      center: true,
      maxWidth: "max-w-4xl",
      children: (
        <RejectPlanView
          plan={originalPlan}
          onCompleted={() => {
            modal.hide();
          }}
        />
      ),
    });
  }

  function handlePlan() {
    modal.present({
      title: "Mark as Handled",
      center: true,
      maxWidth: "max-w-4xl",
      children: <HandlePlanView plan={originalPlan} onCompleted={modal.hide} />,
    });
  }

  function startProcessing() {
    modal.present({
      title: "Start Processing",
      center: true,
      maxWidth: "max-w-4xl",
      children: (
        <StartProcessingView
          plan={originalPlan}
          odooUrl={odooUrl}
          companyId={ODOO_COMPANY_IDS[originalPlan.wisCustomer.system] || 1}
          onCompleted={modal.hide}
        />
      ),
    });
  }

  async function saveHandler() {
    if (!validatePlan()) return;

    setIsSaving(true);
    try {
      const res = await savePlan();
      Alert("success", "Plan Saved.");
      setLines(res.saveProductionPlan.productionPlan.lines);
    } catch (error) {
      Alert("error", parseError(error));
    }
    setIsSaving(false);
  }

  async function savePlan(values = {}) {
    const variables = {
      id: originalPlan.id,
      odooTransferOperationTypeId,
      odooTransferFromLocationId,
      odooTransferToLocationId,
      odooSalesOrderPaymentTermsId,
      odooSalesOrderWarehouseId,
      odooTaxId,
      odooSalesOrderClientOrderRef,
      odooSalesOrderSourceId,
      odooIncoterm,
      odooXDeliverymethod,
      hasPrepaymentInPaymentTerm,
      requiredCertificateTypes,
      description,
      extraData: JSON.stringify(extraData, " ", 2),
      ...values,
    };
    const res = await saveProductionPlan({ variables });
    return res.data;
  }

  console.log("originalPlan", originalPlan);

  async function changeStartDate(day) {
    const prevStartDate = moment(originalPlan.startDate).format("YYYY-MM-DD");
    if (day === prevStartDate) return;

    try {
      await savePlan({ startDate: new Date(day) });
    } catch (error) {
      window.alert(parseError(error));
    }
  }

  function tryRecompute() {
    reComputeModal.present({
      title: "Recompute",
      maxWidth: "max-w-4xl",
      center: true,
      children: (
        <TryRecomputeView
          planId={originalPlan.id}
          onCompleted={() => {
            reComputeModal.hide();
          }}
        />
      ),
    });
  }

  function onChangeLine(id, field, value) {
    const newLines = lines.map((line) =>
      line.id === id ? { ...line, [field]: value, totalCost: 0 } : line,
    );
    setLines(newLines);
  }

  function onChangeLineProduct(id, product, salesPrice) {
    const newLines = lines.map((line) => {
      return line.id === id
        ? {
            ...line,
            product,
            salesPrice,
            discountPercentage: 0,
          }
        : line;
    });
    setLines(newLines);
  }

  function removeLine(id) {
    const newLines = lines.filter((prevLine) => prevLine.id !== id);
    setLines(newLines);
  }

  function tryAddAdditionalPlan() {
    additionalPlanModal.present({
      title: "Addition Plan",
      center: true,
      maxWidth: "max-w-xl",
      children: (
        <CreateAdditionPlanView
          originalPlan={originalPlan}
          complete={(newPlan) => {
            additionalPlanModal.hide();
            navigate(`/order-scheduler/plans/${newPlan.id}`);
          }}
        />
      ),
    });
  }

  function tryDuplicate() {
    modal.present({
      title: "Duplicate Plan",
      center: true,
      maxWidth: "max-w-2xl",
      children: (
        <DuplicatePlanView
          originalPlan={originalPlan}
          complete={(res) => {
            modal.hide();
            navigate(
              `/order-scheduler/plans/${res.duplicateProductionPlan.productionPlan.id}`,
            );
          }}
        />
      ),
    });
  }

  function reduceQty() {
    reduceQtyModal.present({
      title: "Reduce QTY",
      maxWidth: "max-w-6xl",
      children: (
        <ReduceQtyView
          complete={(newLines) => {
            setLines(newLines);
            reduceQtyModal.hide();
          }}
          originalPlan={originalPlan}
        />
      ),
    });
  }

  function showBom(product) {
    bomModal.present({
      title: product.name,
      subtitle: "Production Info",
      maxWidth: "max-w-6xl",
      children: <ProductProductionInfoView id={product.id} />,
    });
  }

  function syncBackToPriceListHandler() {
    syncPriceModal.present({
      title: "Sync Price",
      subtitle: "Sync Price in this plan back to Price List",
      maxWidth: "max-w-6xl",
      children: (
        <SyncPriceToPriceListView
          priceListId={originalPlan.wisPriceList.id}
          lines={originalPlan.lines}
        />
      ),
    });
  }

  function editPlanName() {
    const name = window.prompt("Please enter the new name", originalPlan.name);
    if (name) {
      saveProductionPlan({
        variables: { id: originalPlan.id, name },
        onCompleted() {
          Alert("success", "Name updated.");
        },
        onError(error) {
          Alert("error", error.message);
        },
      });
    }
  }

  const canConfirmPrepayment = [
    "sharon@waboba.com",
    "livi@waboba.com",
  ].includes(user.email);

  const shippingZone = originalPlan.wisCustomer.shippingZone;

  return (
    <PlanContext.Provider
      value={{
        originalPlan,
        currencySymbolString,
        extraData,
        setExtraData,
        showBom,
      }}
    >
      <Page
        title={
          <PlanPageNavigationTitleView
            plan={originalPlan}
            editPlanName={editPlanName}
          />
        }
        backTo={`./..`}
        rightButtons={
          <div className="flex space-x-6">
            {hasPermission() ? (
              <a
                href={`${process.env.REACT_APP_SERVER_ADMIN_URL}product/productionplan/${originalPlan.id}`}
                target="_blank"
                rel="noreferer"
                data-testid="os-admin-link"
              >
                Admin
              </a>
            ) : null}

            <Button
              onClick={syncBackToPriceListHandler}
              disabled={originalPlan.lines === 0}
            >
              Sync Price Back
            </Button>

            <Button onClick={showLogs}>Logs</Button>
            <Button
              disabled={isComputing}
              loading={downloading}
              onClick={downloadPdf}
            >
              Download PDF
            </Button>

            <Button
              onClick={tryDuplicate}
              disabled={isComputing || isRequestingToConfirm}
            >
              Duplicate
            </Button>

            <Button
              onClick={saveHandler}
              loading={isSaving}
              disabled={isComputing || isRequestingToConfirm}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="p-6">
          <ProductionPlanStatusChain status={originalPlan.state} />
        </div>
        <div className="grid grid-cols-12 gap-6 p-6 pt-0">
          <div className="col-span-9 space-y-4 card overflow-visible">
            <h5>Base Info</h5>

            <div className="flex space-x-4 items-center">
              <label>Start Date: </label>

              {["AWAITING_PRODUCTION", "PLANNING"].includes(
                originalPlan.state,
              ) ? (
                <div className="flex items-center space-x-3">
                  <NativeDatePicker
                    value={moment(originalPlan.startDate).format("YYYY-MM-DD")}
                    disabled
                  />
                  <span className="opacity-70 italic pl-2">China Timezone</span>
                  <Button onClick={changePlanStartDate}>
                    Change Start Date
                  </Button>
                </div>
              ) : (
                <div>
                  <NativeDatePicker
                    value={moment(originalPlan.startDate).format("YYYY-MM-DD")}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => changeStartDate(e.target.value)}
                    disabled={disabled || originalPlan.parent}
                  />
                  <span className="opacity-70 italic pl-2">China Timezone</span>
                </div>
              )}
            </div>

            <div className="flex space-x-6">
              <div className="flex space-x-4 items-center">
                <label>Type: </label>
                <PlanTypeSelector value={originalPlan.planType} disabled />
              </div>

              {originalPlan.planType === "AB_INTERNAL_TRANSFER" && (
                <div>
                  <label className="pr-3">Operation Type: </label>

                  <OdooPickingTypeSelector
                    value={odooTransferOperationTypeId}
                    onSelect={onChangeStockPickingType}
                    filterFunction={(i) =>
                      i[0] ===
                        ODOO_OPERATION_TYPE_IDS.CHINA_INTERNAL_TRANSFER ||
                      i[0] ===
                        ODOO_OPERATION_TYPE_IDS.CAMBODIA_INTERNAL_TRANSFER
                    }
                  />
                </div>
              )}
            </div>

            <div className="col-span-12 space-y-4">
              {originalPlan.planType !== "AB_INTERNAL_TRANSFER" && (
                <div className="flex space-x-6 items-center">
                  {originalPlan.planType === "AB_LTD_SALES_ORDER" && (
                    <div className="flex space-x-4 items-center">
                      <label>Customer: </label>
                      <Link
                        to={`/sales/customers/${originalPlan.wisCustomer.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {originalPlan.wisCustomer.name} [
                        {originalPlan.wisCustomer.system}]
                      </Link>
                    </div>
                  )}

                  <div className="flex space-x-4 items-center">
                    <label>Price List: </label>
                    {originalPlan.wisPriceList ? (
                      <Link
                        to={`/sales/price-lists/${originalPlan.wisPriceList.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {originalPlan.wisPriceList.name} [
                        {originalPlan.wisPriceList.currency}]
                      </Link>
                    ) : (
                      <span className="font-semibold text-red-600">
                        Price list not set.
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="flex space-x-4">
                  <label className="pr-3">Required Certificates: </label>
                  {COMPLIANCE_DOCUMENTS_OPTIONS.map((option) => (
                    <div key={option} className="flex items-center">
                      <input
                        disabled={disabled || originalPlan.parent}
                        type="checkbox"
                        id={option}
                        name={option}
                        value={option}
                        checked={planRequiredCertificates.includes(option)}
                        onChange={(e) => {
                          const newRequiredCertificateTypes = e.target.checked
                            ? [...planRequiredCertificates, option]
                            : planRequiredCertificates.filter(
                                (o) => o !== option,
                              );
                          setRequiredCertificateTypes(
                            newRequiredCertificateTypes.join(","),
                          );
                        }}
                      />
                      <label htmlFor={option} className="ml-2">
                        {option}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="opacity-70 mt-1">
                  WIS will automatically validate the products in the order
                  against the selected certificates.
                </div>
              </div>

              <div className="space-y-4">
                {originalPlan.planType !== "AB_INTERNAL_TRANSFER" && (
                  <>
                    <div>
                      <label className="pr-3">Warehouse: </label>
                      <OdooWarehouseSelector
                        company={originalPlan.wisCustomer.system}
                        disabled={disabled || originalPlan.parent}
                        value={odooSalesOrderWarehouseId}
                        onSelect={setOdooSalesOrderWarehouseId}
                        filterFunction={(i) =>
                          Object.values(ODOO_WAREHOUSE_IDS).includes(
                            Number(i.id),
                          )
                        }
                      />
                    </div>
                    <div className="flex items-center space-x-6">
                      <div>
                        <label className="pr-3">Customer Ref: </label>
                        <Input
                          value={odooSalesOrderClientOrderRef || ""}
                          onChange={(e) =>
                            setOdooSalesOrderClientOrderRef(e.target.value)
                          }
                        />
                      </div>

                      {originalPlan.wisCustomer.system === "LTD" && (
                        <div>
                          <label className="pr-3">Source Ref: </label>
                          <Select
                            value={odooSalesOrderSourceId || "0"}
                            onChange={(e) =>
                              setOdooSalesOrderSourceId(e.target.value)
                            }
                          >
                            <option value="0" disabled>
                              -- Select a Source --
                            </option>
                            {Object.entries(ODOO_LTD_SOURCES).map(
                              ([key, value]) => (
                                <option key={key} value={key}>
                                  {value}
                                </option>
                              ),
                            )}
                          </Select>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {originalPlan.planType === "AB_INTERNAL_TRANSFER" && (
                  <div className="space-x-3">
                    <label htmlFor="">Transfer from</label>

                    <OdooStockLocationSelector
                      disabled
                      value={odooTransferFromLocationId}
                      onSelect={setOdooTransferFromLocationId}
                      filterFunction={(i) =>
                        i.id === ODOO_LOCATION_IDS.CHINA_STOCK ||
                        i.id === ODOO_LOCATION_IDS.CAMBODIA_STOCK
                      }
                    />
                    <label htmlFor="">to </label>
                    <OdooStockLocationSelector
                      disabled={disabled || originalPlan.parent}
                      value={odooTransferToLocationId}
                      onSelect={setOdooTransferToLocationId}
                      filterFunction={(i) =>
                        i.display_name.includes("/Transit")
                      }
                    />
                  </div>
                )}

                {originalPlan.planType === "AB_LTD_SALES_ORDER" && (
                  <>
                    <div className="flex space-x-8 items-center">
                      <div>
                        <label className="pr-3">Payment Terms: </label>
                        <OdooPaymentTermsSelector
                          company={originalPlan.wisCustomer.system || null}
                          value={odooSalesOrderPaymentTermsId}
                          onSelect={setOdooSalesOrderPaymentTermsId}
                        />
                      </div>
                      <div className="flex items-center">
                        <label className="pr-3" htmlFor="id-has-prepayment">
                          Has Prepayment:{" "}
                        </label>
                        <input
                          id="id-has-prepayment"
                          type="checkbox"
                          checked={hasPrepaymentInPaymentTerm}
                          onChange={(e) =>
                            setHasPrepaymentInPaymentTerm(e.target.checked)
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label className="pr-3">Tax: </label>
                      <OdooTaxSelector
                        company={originalPlan.wisCustomer.system || null}
                        value={odooTaxId}
                        onSelect={setOdooTaxId}
                      />
                    </div>

                    <div className="flex space-x-8 items-center">
                      <div>
                        <label className="pr-3">Delivery Method: </label>
                        <OdooDeliveryMethodSelector
                          company={originalPlan.wisCustomer.system}
                          value={odooXDeliverymethod}
                          onSelect={setOdooXDeliverymethod}
                        />
                      </div>
                      <div>
                        <label className="pr-3">Incoterm: </label>
                        <OdooIncotermsSelector
                          company={originalPlan.wisCustomer.system}
                          value={odooIncoterm}
                          onSelect={setOdooIncoterm}
                        />
                      </div>
                    </div>
                  </>
                )}

                {originalPlan.planType.includes("INC") && <IncPoView />}

                <div>
                  <div>
                    <label className="pr-3">Note: </label>
                  </div>
                  <Text
                    className="mt-2"
                    placeholder="Remark"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                  <div className="opacity-70 ml-1">
                    This note will be added to AB odoo Sales Order.
                  </div>
                </div>

                {!originalPlan.parent &&
                  originalPlan.planType === "INC_FOB_ORDER" && (
                    <div className="pt-4">
                      <INCFOBOrderView />
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="col-span-3 space-y-6 flex flex-col">
            {originalPlan.state === "HANDLED" && (
              <div className="card">
                <h5>Status</h5>
                <div className="mt-2 text-sm">
                  <div>
                    This plan has been confirmed and handled. Please start
                    processing.
                  </div>

                  <div className="space-y-2 mt-2">
                    <div className="flex space-x-2">
                      <label>Handed by: </label>
                      <span>{originalPlan.handledBy.firstName}</span>
                    </div>

                    {originalPlan.handleRemarks && (
                      <div>{originalPlan.handleRemarks}</div>
                    )}

                    <hr />

                    <Button onClick={startProcessing}>Start Processing</Button>
                  </div>
                </div>
              </div>
            )}

            {originalPlan.state === "PROCESSING" && originalPlan.handledBy ? (
              <div className="card">
                <h5>Processing Info</h5>
                <div className="mt-1">
                  <div className="flex space-x-2">
                    <label>Handed by: </label>
                    <span>{originalPlan.handledBy.firstName}</span>
                  </div>

                  {originalPlan.handleRemarks && (
                    <div className="mt-1">{originalPlan.handleRemarks}</div>
                  )}
                </div>
              </div>
            ) : null}

            {!["HANDLED", "PROCESSING"].includes(originalPlan.state) && (
              <div className="card text-sm">
                <h5>Actions</h5>

                <div className="divide-y divide-gray-100 dark:divide-gray-700 mt-2 space-y-4">
                  <div>
                    {originalPlan.state === "DRAFT" && (
                      <div className="space-y-2">
                        {!originalPlan.readyDate && (
                          <div className="text-xs font-semibold text-orange-600 mt-2">
                            {NEED_TO_RECOMPUTE_MESSAGE}
                          </div>
                        )}

                        {originalPlan.skipCustomerStages ? (
                          <>
                            <div className="opacity-70 text-xs">
                              For Waboba INC, internal transfer and additional
                              plans, we skip the customer confirmation stages.
                              Click `Request Confirm` button to request
                              production team confirm the plan. Sales and
                              customer stages will be skipped.
                            </div>

                            <Button
                              onClick={salesConfirmHandler}
                              loading={updatePlanRes.loading}
                              disabled={
                                isComputing ||
                                isSaving ||
                                originalPlan.readyDate === null
                              }
                            >
                              Request Confirm
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="opacity-70 text-xs">
                              Click `Sales Confirm` button to confirm the plan,
                              then wis will create or update an SO Quotation in
                              odoo. You can send this quotation to customer for
                              confirmation.
                            </div>
                            <Button
                              onClick={salesConfirmHandler}
                              loading={updatePlanRes.loading}
                              disabled={
                                !originalPlan.readyDate ||
                                isComputing ||
                                isSaving
                              }
                            >
                              Sales Confirm
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {originalPlan.state === "SALES_CONFIRMED" && (
                      <div className="space-y-2">
                        <div className="opacity-70 text-xs">
                          This plan has been confirmed by sales, please send it
                          to customer for confirmation. Then click `Mark as
                          Awaiting Customer` for this plan.
                        </div>
                        <Button
                          className="mt-2"
                          onClick={sentToCustomerHandler}
                          loading={updatePlanRes.loading}
                          disabled={
                            isComputing ||
                            isSaving ||
                            originalPlan.readyDate === null
                          }
                        >
                          Mark as Awaiting Customer
                          {originalPlan.nextActionUser && (
                            <>
                              {" "}
                              (
                              <b className="text-orange-600">
                                {originalPlan.nextActionUser.firstName}
                              </b>
                              )
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {originalPlan.state === "AWAITING_CUSTOMER" && (
                      <div className="space-y-2">
                        <div className="text-xs">
                          Once customer and prepayment(if there is) confirmed,
                          click `Customer Confirm` button to confirm the plan.
                          The production team will start processing this plan.
                        </div>

                        {originalPlan.customerConfirmAt ? (
                          <div className="text-green-600 flex space-x-2 items-center">
                            <span>Confirmed by Customer</span>
                            <BsCheckCircleFill />
                          </div>
                        ) : (
                          <Button
                            onClick={() =>
                              customerConfirmHandler("CUSTOMER_CONFIRM")
                            }
                            loading={updatePlanRes.loading}
                            disabled={
                              isComputing ||
                              isSaving ||
                              originalPlan.readyDate === null
                            }
                          >
                            Confirm by Customer (
                            <b className="text-orange-600">
                              {originalPlan.createdBy.firstName}
                            </b>
                            )
                          </Button>
                        )}

                        {originalPlan.hasPrepaymentInPaymentTerm && (
                          <div className="pt-2">
                            <div className="text-xs border-t border-gray-100 dark:border-gray-700 pt-4">
                              There is prepayment in payment term for this plan,
                              please confirm we have received the prepayment
                              before confirm this plan.
                            </div>

                            <Button
                              className="mt-2"
                              onClick={() =>
                                customerConfirmHandler("PREPAYMENT_RECEIVED")
                              }
                              loading={updatePlanRes.loading}
                              disabled={
                                !originalPlan.customerConfirmAt ||
                                isComputing ||
                                isSaving ||
                                originalPlan.readyDate === null ||
                                !canConfirmPrepayment
                              }
                            >
                              Prepayment Received (
                              <b className="text-orange-600">Sharon</b>)
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {originalPlan.state === "AWAITING_PRODUCTION" && (
                      <div className="space-y-2">
                        <div className="opacity-70 text-xs">
                          Customer has confirmed the plan, please recompute and
                          confirm the plan ASAP.
                        </div>

                        <Button onClick={tryRecompute}>Recompute Plan</Button>

                        {originalPlan.confirmTimeAt ? (
                          <div className="text-green-600 flex space-x-2 items-center">
                            <span>Confirmed Ready Date</span>
                            <BsCheckCircleFill />
                          </div>
                        ) : (
                          <Button
                            disabled={
                              !hasPermission(OS_CONFIRM_TIME) ||
                              isComputing ||
                              isSaving ||
                              isRequestingToConfirm
                            }
                            loading={isConfirmingTime}
                            onClick={tryConfirmTime}
                          >
                            Confirm Time
                          </Button>
                        )}

                        {originalPlan.requiredToConfirmCost ? (
                          <>
                            {originalPlan.confirmCostAt ? (
                              <div className="text-green-600 flex space-x-2 items-center">
                                <span>Confirmed Cost</span>
                                <BsCheckCircleFill />
                              </div>
                            ) : (
                              <div>
                                <Button
                                  disabled={
                                    !originalPlan.confirmTimeAt ||
                                    !hasPermission(OS_CONFIRM_COST) ||
                                    saveProductionPlanRes.loading
                                  }
                                  loading={isConfirmingCost}
                                  onClick={confirmCostHandler}
                                >
                                  Confirm Cost
                                </Button>
                                <div className="text-xs opacity-70">
                                  High value plans are required to confirm cost.
                                  Compare value is{" "}
                                  {originalPlan.costCompareValue.toLocaleString(
                                    "en-US",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}{" "}
                                  based on our history.
                                </div>
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}

                    {originalPlan.state === "PLANNING" && (
                      <div className="space-y-2">
                        <div className="opacity-70 text-xs">
                          This plan is confirmed by production team. You can
                          start handle this plan.
                        </div>
                        <Button onClick={tryRecompute}>Recompute Plan</Button>
                        <Button onClick={handlePlan}>Mark as Handled</Button>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 space-y-2">
                    {[
                      "SALES_CONFIRMED",
                      "AWAITING_CUSTOMER",
                      "AWAITING_PRODUCTION",
                      "PLANNING",
                    ].includes(originalPlan.state) && (
                      <Button danger onClick={rejectPlan}>
                        Reject
                      </Button>
                    )}

                    {originalPlan.state !== "CANCELED" && (
                      <Button danger onClick={cancelPlan}>
                        Cancel Plan
                      </Button>
                    )}
                  </div>

                  <div className="pt-4 text-xs">
                    Plan Created by <b>{originalPlan.createdBy.firstName}</b>
                  </div>
                </div>
              </div>
            )}

            <OdooView disabled={disabled} didSyncedToOdoo={didSyncedToOdoo} />

            {originalPlan.planType.includes("INC") && <IncOdooView />}
          </div>

          <div className="col-span-12">
            <div className="card">
              <h5>Products</h5>

              <div>
                <PlanLines
                  originalPlan={originalPlan}
                  lines={
                    originalPlan.state === "DRAFT" ? lines : originalPlan.lines
                  }
                  disabled={disabled}
                  odooSalesOrderWarehouseId={odooSalesOrderWarehouseId}
                  requiredCertificateTypes={requiredCertificateTypes}
                  setLines={setLines}
                  onChangeLine={onChangeLine}
                  onChangeLineProduct={onChangeLineProduct}
                  removeLine={removeLine}
                  isComputing={isComputing}
                  reduceQty={reduceQty}
                  syncBackToPriceListHandler={syncBackToPriceListHandler}
                />

                <div className="text-right opacity-70 mt-2">
                  The estimated ready date is computed base on the production
                  capacity provided by suppliers. The actual ready date might be
                  +/- 7 days.
                  <br />
                  Since our inventory is dynamic, the estimated ready date might
                  change until you request to confirm.
                </div>

                {computeQueryRes.error ? (
                  <div className="mt-4">
                    <Errors error={computeQueryRes.error} />
                  </div>
                ) : null}

                {originalPlan.state === "DRAFT" ? (
                  <div className="mt-6">
                    <Button
                      primary
                      className="mt-2"
                      onClick={computeHandler}
                      loading={isComputing}
                      disabled={isSaving || isRequestingToConfirm}
                    >
                      {isComputing ? "Computing" : "Compute"} Ready Date
                    </Button>
                  </div>
                ) : null}

                {!originalPlan.readyDate ? (
                  <div className="mt-3 text-xs font-semibold text-orange-600">
                    {NEED_TO_RECOMPUTE_MESSAGE}
                  </div>
                ) : null}

                {originalPlan.readyDate && !isComputing ? (
                  <div className="mt-6 space-y-4">
                    <CharlesButtonWithArrow
                      show={showProductions}
                      onClick={() => setShowProductions((prev) => !prev)}
                    >
                      Production Detail
                    </CharlesButtonWithArrow>

                    {showProductions ? (
                      <ProductionsView planId={originalPlan.id} />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {originalPlan.parent ? null : (
              <AdditionPlansView
                id={originalPlan.id}
                tryAddAdditionalPlan={tryAddAdditionalPlan}
              />
            )}

            {originalPlan.parent ? (
              <div className="card mt-8">
                <h5>Total Capacity</h5>
                <div className="mt-1">
                  This is an additional plan, capacity shows the total cbm
                  capacity for the original plan, including other additional
                  plans if there is any.
                </div>
                <div className="mt-4">
                  <ContainerizationView
                    totalCbm={
                      originalPlan.totalCbm +
                      originalPlan.parent.totalCbm +
                      originalPlan.children
                        .filter((i) => i.id !== originalPlan.id)
                        .reduce((res, i) => res + i.totalCbm, 0)
                    }
                    shippingZone={shippingZone}
                    defaultDisabledContainers={["40GP"]}
                  />
                </div>
              </div>
            ) : originalPlan.children.length > 0 ? (
              <div className="card mt-8">
                <CombinedPlanSummary
                  id={originalPlan.id}
                  shippingZone={shippingZone}
                />
              </div>
            ) : (
              <div className="card mt-8">
                <h5>Capacity</h5>
                {originalPlan.totalCbm > 0 && (
                  <div className="mt-4">
                    <ContainerizationView
                      totalCbm={originalPlan.totalCbm}
                      shippingZone={shippingZone}
                      defaultDisabledContainers={["40GP"]}
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <AboutOSView />
            </div>
          </div>
        </div>
      </Page>
    </PlanContext.Provider>
  );
};

export default PlanForm;
